import { useEffect, useState } from "react";
import { Crown, Check, Sparkles, ArrowRight, Star, Tag, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/App";
import { toast } from "sonner";

const planConfig = {
  free: {
    name: "Free", color: "var(--text-tertiary)", tag: "Inizia subito",
    features: [
      "Fino a 2 membri famiglia",
      "Fino a 50 documenti",
      "1 GB storage",
      "20 richieste AI/giorno",
      "Chat famiglia base",
      "Calendario base",
      "Lista spesa condivisa",
      "Reminder semplici",
    ],
  },
  standard: {
    name: "Family", color: "#E07A5F", recommended: true, tag: "Più popolare",
    features: [
      "Membri famiglia illimitati",
      "Documenti illimitati",
      "Memo AI completa nella chat",
      "OCR documenti",
      "Trascrizione vocali",
      "Ricerca avanzata",
      "Reminder smart",
      "Timeline famiglia",
      "Backup cloud",
      "Lista spesa AI",
      "Calendario completo",
      "Sync multi-device",
    ],
  },
  premium: {
    name: "Family AI+", color: "#F2CC8F", tag: "AI avanzata",
    features: [
      "Tutto di Family, più:",
      "Memo AI avanzato e proattivo",
      "Automazioni intelligenti",
      "Suggerimenti predittivi",
      "Analisi delle conversazioni",
      "AI illimitata",
      "Assistente vocale",
      "Storico illimitato",
      "Report PDF mensili",
      "Analytics organizzazione",
      "Priorità supporto",
    ],
  },
};

export default function PricingPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [planStatus, setPlanStatus] = useState(null);
  const [upgrading, setUpgrading] = useState(null);
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (user) api.get("/plan/status").then(({ data }) => setPlanStatus(data));
  }, [user]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("code", couponCode.trim().toUpperCase());
      const { data } = await api.post("/coupons/validate", fd);
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applicato!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coupon non valido");
      setAppliedCoupon(null);
    } finally {
      setValidating(false);
    }
  };

  const upgrade = async (planKey) => {
    if (!user) { navigate("/auth"); return; }
    if (planKey === "free") { navigate("/app"); return; }
    const packageId = `${planKey}_${billing}`;
    setUpgrading(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio pagamento");
      setUpgrading(null);
    }
  };

  const currentPlan = planStatus?.plan || "free";
  const onTrial = planStatus?.on_trial;

  const getPriceDisplay = (planKey) => {
    if (planKey === "free") return { main: "€0", suffix: "per sempre" };
    const monthly = { standard: 4.99, premium: 9.99 }[planKey];
    const yearly = { standard: 49.0, premium: 99.0 }[planKey];
    let amount = billing === "yearly" ? yearly / 12 : monthly;
    if (appliedCoupon) {
      if (appliedCoupon.discount_percent) amount = amount * (1 - appliedCoupon.discount_percent / 100);
      if (appliedCoupon.discount_amount) amount = Math.max(0.5, amount - appliedCoupon.discount_amount);
    }
    return {
      main: `€${amount.toFixed(2)}`,
      suffix: "/mese",
      yearlyTotal: billing === "yearly" ? `€${yearly.toFixed(0)}/anno` : null,
      saves: billing === "yearly" ? `${Math.round((1 - yearly / (monthly * 12)) * 100)}% risparmio` : null,
    };
  };

  return (
    <div className="px-5 md:px-10 py-10 max-w-6xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        data-testid="pricing-back-button"
        className="text-sm text-tertiary-fg hover:text-primary-fg mb-6"
      >
        ← Indietro
      </button>

      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Family Brain</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3">
          Scegli come ricordare<br />
          <span className="text-brand">la vostra famiglia.</span>
        </h1>
        <p className="text-secondary-fg text-base mt-4 max-w-xl mx-auto">
          Inizia gratis. Annulla quando vuoi.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex items-center bg-surface border border-subtle rounded-full p-1 mt-6">
          <button
            data-testid="billing-monthly"
            onClick={() => setBilling("monthly")}
            className={`px-5 py-1.5 rounded-full text-sm transition-colors ${billing === "monthly" ? "bg-brand text-white" : "text-tertiary-fg"}`}
          >
            Mensile
          </button>
          <button
            data-testid="billing-yearly"
            onClick={() => setBilling("yearly")}
            className={`px-5 py-1.5 rounded-full text-sm transition-colors ${billing === "yearly" ? "bg-brand text-white" : "text-tertiary-fg"}`}
          >
            Annuale <span className="text-amber-warm text-[10px] font-semibold ml-1">-17%</span>
          </button>
        </div>

        {onTrial && (
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-elevated border border-amber-warm/30 text-sm">
            <Crown className="w-4 h-4 text-amber-warm" />
            Trial Premium attivo · {planStatus.trial_days_left} giorni rimasti
          </div>
        )}
      </div>

      {/* Coupon input */}
      <div className="max-w-md mx-auto mb-8 flex gap-2">
        <div className="relative flex-1">
          <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
          <input
            data-testid="coupon-input"
            placeholder="Hai un codice sconto?"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            className="input-warm pl-10 uppercase"
            disabled={!!appliedCoupon}
          />
        </div>
        {appliedCoupon ? (
          <button
            data-testid="coupon-clear"
            onClick={() => { setAppliedCoupon(null); setCouponCode(""); }}
            className="btn-secondary !px-4"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="coupon-apply"
            onClick={validateCoupon}
            disabled={!couponCode.trim() || validating}
            className="btn-primary !px-4"
          >
            {validating ? "..." : "Applica"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(planConfig).map(([key, plan], idx) => {
          const isCurrent = currentPlan === key;
          const price = getPriceDisplay(key);
          const pkgId = key === "free" ? null : `${key}_${billing}`;
          return (
            <div
              key={key}
              data-testid={`plan-card-${key}`}
              className={`relative rounded-3xl p-6 border transition-all animate-fade-up ${
                plan.recommended
                  ? "bg-surface border-brand shadow-[0_0_0_2px_rgba(224,122,95,0.25)] md:scale-105"
                  : "bg-surface border-subtle"
              }`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1">
                  <Star className="w-3 h-3 fill-white" /> {plan.tag}
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-11 h-11 rounded-2xl grid place-items-center"
                  style={{ backgroundColor: plan.recommended ? plan.color : "var(--bg-elevated)" }}
                >
                  {key === "free" && <Sparkles className="w-5 h-5" style={{ color: plan.color }} strokeWidth={1.6} />}
                  {key === "standard" && <Star className="w-5 h-5 text-white" strokeWidth={1.6} />}
                  {key === "premium" && <Crown className="w-5 h-5" style={{ color: plan.color }} strokeWidth={1.6} />}
                </div>
                <div>
                  <div className="font-display text-xl font-semibold">{plan.name}</div>
                  {!plan.recommended && <div className="text-xs text-tertiary-fg">{plan.tag}</div>}
                </div>
              </div>

              <div className="mb-1">
                <span className="font-display text-4xl font-semibold">{price.main}</span>
                <span className="text-tertiary-fg text-sm ml-1">{price.suffix}</span>
              </div>
              {price.yearlyTotal && (
                <div className="text-xs text-tertiary-fg mb-1">{price.yearlyTotal} · <span className="text-sage">{price.saves}</span></div>
              )}
              {appliedCoupon && key !== "free" && (
                <div className="text-xs text-amber-warm mb-1">Coupon {appliedCoupon.code} applicato</div>
              )}
              <div className="mb-4" />

              <button
                onClick={() => upgrade(key)}
                disabled={isCurrent || upgrading === pkgId || (key === "free" && (currentPlan === "free" || onTrial))}
                data-testid={`plan-cta-${key}`}
                className={`w-full rounded-full py-2.5 font-medium transition-all ${
                  (isCurrent || (key === "free" && (currentPlan === "free" || onTrial)))
                    ? "bg-elevated text-tertiary-fg cursor-default"
                    : plan.recommended
                    ? "bg-brand text-white hover:bg-[var(--brand-hover)] hover:-translate-y-0.5"
                    : "bg-elevated text-primary-fg border border-subtle hover:bg-[var(--border-subtle)]"
                }`}
              >
                {key === "free" && (currentPlan === "free" || onTrial) ? "Piano corrente" :
                 isCurrent ? "Piano attuale" :
                 upgrading === pkgId ? "Reindirizzamento..." :
                 key === "free" ? "Inizia gratis" :
                 `Passa a ${plan.name}`}
              </button>

              <div className="mt-6 space-y-2">
                {plan.features.map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: plan.recommended ? plan.color : "var(--sage)" }} />
                    <span className="text-secondary-fg">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 bg-surface border border-subtle rounded-3xl p-6 max-w-2xl mx-auto text-center">
        <Crown className="w-8 h-8 text-amber-warm mx-auto mb-3" strokeWidth={1.4} />
        <h3 className="font-display text-xl font-medium">7 giorni Premium gratis</h3>
        <p className="text-sm text-secondary-fg mt-2 leading-relaxed">
          Ogni nuovo account riceve automaticamente 7 giorni di Premium. Nessuna carta richiesta.
          Costruisci la memoria della tua famiglia, poi scegli il piano che fa per te.
        </p>
      </div>

      <p className="text-center text-xs text-tertiary-fg mt-10">
        Puoi annullare in qualsiasi momento · Pagamenti sicuri via Stripe
      </p>
    </div>
  );
}
